import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import SectionMessage from '@atlaskit/section-message';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useAiBulkNavigationGotoPage } from '../../providers/AiBulkNavigationContextProvider.tsx';
import {
	useBulkCreateContext,
	useGetIssueType,
	ErrorMessageMap,
	ErrorStates,
	useSetUserChoseManualCreation,
} from '../../providers/BulkCreateContextProvider.tsx';
import { messages } from '../../messages.ts';
import { BulkCreateErrorSectionMessage } from '../bulk-work-create-common/BulkCreateErrorSectionMessage.tsx';
import {
	useBulkCreateLoggingEvents,
	BULK_ISSUE_CREATE_SOURCE,
} from '../../utils/useBulkCreateLoggingEvents.tsx';
import { useGetUnmappedRequiredFields } from '../../services/useGetUnmappedRequiredFields.tsx';
import { useIsAIIssueCreationAllowed } from '../../utils/useIsAIIssueCreationAllowed.tsx';
import {
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import type { FieldMetadata } from '../../__types__/apiUtils.ts';
import { calculateExpectedIssueIndices } from '../../utils/bulkCreateSelectionUtils.ts';
import { getIsCreateIssueWithAiEnabledByUser } from '../../utils/localStorageHelpers.ts';
import { IssueCreateWithAiToggle } from '../ai-work-create/IssueCreateWithAiToggle.tsx';
import { IssueCreateAiLoading } from '../ai-work-create/IssueCreateAiLoading.tsx';
import { WorkItemCreationError } from '../work-create-common/WorkItemCreationError.tsx';
import { useUnmappedWorkflowValidators } from '../../services/useUnmappedWorkflowValidators.tsx';

import { BulkCreateIssueCard } from './BulkCreateIssueCard.tsx';

const bodyStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

// Used for both AI and non-AI bulk create
export const BulkCreateBody = () => {
	const goToPage = useAiBulkNavigationGotoPage();

	const [
		{
			bulkIssuesList,
			currentJiraSite,
			currentJiraProject,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			aiResultViewedFiredOnce,
			aiState,
			expectedIssueIndices,
			error,
			userChoseManualCreation,
		},
		{
			setAiResultViewedFiredOnce,
			setIsCreatedWithAi,
			setAiState,
			setExpectedIssueIndices,
			setError,
			setBulkIssuesList,
		},
	] = useBulkCreateContext();

	const [{ pageMode, hasEditPermission, contentId, selectedTable, selectedActionItems }] =
		useIssueCreateSidePanelContext();

	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { logAiResultViewed } = useBulkCreateLoggingEvents();
	const unmappedRequiredFields: FieldMetadata[] = useGetUnmappedRequiredFields();
	const hasUnmappedRequiredFields = unmappedRequiredFields.length > 0;
	const unmappedWorkflowValidatorFields: string[] = useUnmappedWorkflowValidators();
	const hasUnmappedWorkflowValidatorFields =
		unmappedWorkflowValidatorFields.length > 0 &&
		fg('confluence-jira-work-create-workflow-validators');
	const [showCreateWithAiToggle, setShowCreateWithAiToggle] = useState(false);
	const setUserChoseManualCreation = useSetUserChoseManualCreation();
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const isAiLoading = aiState === 'loading';
	const isAiIssueGenerationError = aiState === 'error' && error === ErrorStates.ISSUE_GENERATION;
	const issueType = useGetIssueType();

	// TODO: remove this once confluence-create-work-items-from-action-items is cleaned up
	useIsAIIssueCreationAllowed({ contentId, isBulkCreate: true });

	useEffect(() => {
		/*
		 * When ConfluenceAI=OFF & JiraAI=ON this component will manually create the issue first until
		 * selected site & project info is available to check Jira's AI enablement.  If JiraAI=ON then
		 * we can setIsCreatedWithAi to what the user's personal AI enablement to trigger a rerender.
		 */
		if (
			aiIssueCreationEnablement?.isEnabled &&
			(isCreatedFromActionItems || isCreatedFromTable) &&
			fg('confluence-create-work-items-from-action-items')
		) {
			const issueIndices = calculateExpectedIssueIndices(
				isCreatedFromTable,
				isCreatedFromActionItems,
				selectedTable,
				selectedActionItems,
			);
			void setExpectedIssueIndices(issueIndices);
			setShowCreateWithAiToggle(true);
			// Keep toggle unchecked despite enablement if user chose manual creation
			if (userChoseManualCreation && fg('confluence-ai-jira-work-create-fast-follows')) {
				void setIsCreatedWithAi(false);
				void setAiState('idle');
			} else {
				// We can only create 1-10 issues with AI at a time
				const newIsCreatedWithAi =
					getIsCreateIssueWithAiEnabledByUser() &&
					issueIndices.length > 0 &&
					issueIndices.length <= 10;
				void setIsCreatedWithAi(newIsCreatedWithAi);
				if (!newIsCreatedWithAi) void setAiState('idle');
			}
		} else {
			setShowCreateWithAiToggle(false);
		}
	}, [
		isCreatedFromActionItems,
		isCreatedFromTable,
		aiIssueCreationEnablement?.isEnabled,
		setIsCreatedWithAi,
		setAiState,
		setExpectedIssueIndices,
		selectedTable,
		selectedActionItems,
		userChoseManualCreation,
	]);

	const onConfigureClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
			event.preventDefault();
			void goToPage('configure');
		},
		[goToPage],
	);

	useEffect(() => {
		// TODO: Change this before rolling out confluence_ai_bulk_issue_creation_handle_chunks.
		if (isCreatedWithAi && bulkIssuesList.length > 0 && !aiResultViewedFiredOnce) {
			logAiResultViewed(bulkIssuesList.length);
			void setAiResultViewedFiredOnce(true);
		}
	}, [
		isCreatedWithAi,
		bulkIssuesList,
		logAiResultViewed,
		aiResultViewedFiredOnce,
		setAiResultViewedFiredOnce,
	]);

	const resetIssueCreationErrorStates = useCallback(
		(manualCreationChosen: boolean) => {
			void setError(ErrorStates.EMPTY);
			void setBulkIssuesList([]);
			void setUserChoseManualCreation(manualCreationChosen);
		},
		[setError, setBulkIssuesList, setUserChoseManualCreation],
	);

	const handleCreateManually = useCallback(
		(afterIssueCreationError: boolean = false) => {
			if (afterIssueCreationError) {
				resetIssueCreationErrorStates(true);
			}
			void setIsCreatedWithAi(false);
			void setAiState('idle');
			setShowCreateWithAiToggle(false);
		},
		[setIsCreatedWithAi, setAiState, resetIssueCreationErrorStates],
	);

	const handleCreateWithAi = useCallback(
		(afterIssueCreationError: boolean = false) => {
			if (afterIssueCreationError) {
				resetIssueCreationErrorStates(false);
			}
			void setIsCreatedWithAi(true);
			void setAiState('loading');
			setShowCreateWithAiToggle(false);
		},
		[setIsCreatedWithAi, setAiState, resetIssueCreationErrorStates],
	);

	const onAiToggleChange = useCallback(
		(enabled: boolean) => {
			if (enabled) {
				trackAIInteractionInit({
					attributes: {
						pageMode,
						hasEditPermission,
						aiEnablement: aiIssueCreationEnablement?.enablement,
					},
				});
				handleCreateWithAi();
			} else {
				handleCreateManually();
			}
		},
		[
			handleCreateWithAi,
			handleCreateManually,
			pageMode,
			hasEditPermission,
			aiIssueCreationEnablement?.enablement,
			trackAIInteractionInit,
		],
	);

	const creationContext = () => {
		if (isCreatedFromTable) return 'table';
		if (isCreatedFromActionItems) return 'list';
		return 'other';
	};

	const manualCreationDisabled = bulkIssuesList.length > 50;
	const aiCreationDisabled = expectedIssueIndices.length > 10;

	if (fg('confluence-ai-jira-work-create-fast-follows')) {
		// Early return to display loading screen. Nothing else should be displayed until the loading state is complete.
		if (isAiLoading) {
			return (
				<Box padding="space.200" paddingBlockStart="space.100" xcss={bodyStyles}>
					<Stack space="space.200">
						<IssueCreateAiLoading loadingType="bulk" />
					</Stack>
				</Box>
			);
		}

		// Early return to display "try again" error screen
		if (isAiIssueGenerationError) {
			return (
				<WorkItemCreationError
					creationSource={BULK_ISSUE_CREATE_SOURCE}
					errorMessage={ErrorMessageMap[error]()}
					onTryAgain={() => handleCreateWithAi(true)}
					onCreateManually={() => handleCreateManually(true)}
					isCreatedWithAi
				/>
			);
		}

		const showConfigAndCards =
			!!currentJiraSite && !!currentJiraProject && bulkIssuesList.length > 0;

		// AI bulk creation is disabled if there are more than 10 issues in the selection
		// The user workaround is to create the issues manually, or delete items from the table or action item list
		// Approving or deleting items individually will not work, as we cannot create AI issues using incomplete portions of the selected content

		const aiLimitWarningMessage =
			showCreateWithAiToggle &&
			aiCreationDisabled &&
			!manualCreationDisabled &&
			fg('confluence-create-work-items-from-action-items') ? (
				<SectionMessage title={messages.moreThanTenAIWorkItemsSectionMessageTitle.defaultMessage}>
					<Text>
						<FormattedMessage
							{...messages.moreThanTenAIWorkItemsSectionMessageDescription}
							values={{
								context: creationContext(),
							}}
						/>
					</Text>
				</SectionMessage>
			) : null;

		const aiToggle = showCreateWithAiToggle ? (
			<IssueCreateWithAiToggle
				onChange={onAiToggleChange}
				isDisabled={
					expectedIssueIndices.length > 10 && fg('confluence-create-work-items-from-action-items')
				}
			/>
		) : null;

		const jiraSiteInfo = showConfigAndCards ? (
			<Inline space="space.050">
				<Text color="color.text.subtlest" weight="semibold">
					<FormattedMessage {...messages.jiraSiteHeading} />
				</Text>
				<Text color="color.text.subtlest">{currentJiraSite.displayName}</Text>
			</Inline>
		) : null;

		const jiraProjectInfo = showConfigAndCards ? (
			<Inline space="space.050">
				<Text color="color.text.subtlest" weight="semibold">
					<FormattedMessage
						{...(expVal('project-terminology-refresh', 'isEnabled', false)
							? messages.jiraProjectHeadingGalaxia
							: messages.jiraProjectHeading)}
					/>
				</Text>
				<Text color="color.text.subtlest">{currentJiraProject.name}</Text>
			</Inline>
		) : null;

		const configureButton = showConfigAndCards ? (
			<Box>
				<Button spacing="compact" testId="bulk-create-configure-button" onClick={onConfigureClick}>
					<FormattedMessage {...messages.bulkCreateConfigureButtonText} />
				</Button>
			</Box>
		) : null;

		const requiredFieldsWarning = hasUnmappedRequiredFields ? (
			<SectionMessage title={messages.requiredFieldsSectionMessageTitle.defaultMessage}>
				<Text>
					{fg('confluence-issue-terminology-refresh') ? (
						<FormattedMessage
							{...messages.requiredFieldsSectionMessageDescriptionIssueTermRefresh}
						/>
					) : (
						<FormattedMessage {...messages.requiredFieldsSectionMessageDescription} />
					)}
				</Text>
			</SectionMessage>
		) : null;

		const workflowValidatorFieldsWarning = hasUnmappedWorkflowValidatorFields ? (
			<SectionMessage title={messages.workflowValidatorFieldsSectionMessageTitle.defaultMessage}>
				<Stack space="space.100">
					<Text>
						<FormattedMessage
							{...messages.workflowValidatorFieldsSectionMessageDescriptionIssueTermRefresh}
						/>
					</Text>
					<Box as="ul">
						{unmappedWorkflowValidatorFields.map((fieldName) => (
							<Box as="li" key={fieldName}>
								<Text>{fieldName}</Text>
							</Box>
						))}
					</Box>
				</Stack>
			</SectionMessage>
		) : null;

		const manualCreationLimitWarning =
			(isCreatedFromTable ||
				(isCreatedFromActionItems && fg('confluence-create-work-items-from-action-items'))) &&
			manualCreationDisabled ? (
				<SectionMessage title={messages.moreThanFiftyWorkItemsSectionMessageTitle.defaultMessage}>
					<Text>
						<FormattedMessage
							{...messages.moreThanFiftyWorkItemsSectionMessageDescription}
							values={{
								context: creationContext(),
							}}
						/>
					</Text>
				</SectionMessage>
			) : null;

		const issueCards = showConfigAndCards ? (
			<Stack space="space.100">
				{bulkIssuesList.map((issue) => (
					<BulkCreateIssueCard key={issue.id} issue={issue} issueType={issueType} />
				))}
			</Stack>
		) : null;

		return (
			<Box padding="space.200" paddingBlockStart="space.100" xcss={bodyStyles}>
				<Stack space="space.200">
					{aiLimitWarningMessage}
					{aiToggle}
					<Stack space="space.100">
						<BulkCreateErrorSectionMessage />
						{jiraSiteInfo}
						{jiraProjectInfo}
						{configureButton}
						{requiredFieldsWarning}
						{workflowValidatorFieldsWarning}
						{manualCreationLimitWarning}
					</Stack>
					{issueCards}
				</Stack>
			</Box>
		);
	}

	// Old implementation - remove when cleaning up confluence-ai-jira-work-create-fast-follows
	return (
		<Box padding="space.200" paddingBlockStart="space.100" xcss={bodyStyles}>
			<Stack space="space.200">
				{/* AI bulk creation is disabled if there are more than 10 issues in the selection
				The user workaround is to create the issues manually, or delete items from the table or action item list
				Approving or deleting items individually will not work, as we cannot create AI issues using incomplete portions of the selected content
				 */}
				{!isAiLoading &&
					showCreateWithAiToggle &&
					aiCreationDisabled &&
					!manualCreationDisabled &&
					fg('confluence-create-work-items-from-action-items') && (
						<SectionMessage
							title={messages.moreThanTenAIWorkItemsSectionMessageTitle.defaultMessage}
						>
							<Text>
								<FormattedMessage
									{...messages.moreThanTenAIWorkItemsSectionMessageDescription}
									values={{
										context: creationContext(),
									}}
								/>
							</Text>
						</SectionMessage>
					)}
				{!isAiLoading && showCreateWithAiToggle && (
					<IssueCreateWithAiToggle
						onChange={onAiToggleChange}
						// AI creation is disabled if there are more than 10 issues in the selection
						isDisabled={
							expectedIssueIndices.length > 10 &&
							fg('confluence-create-work-items-from-action-items')
						}
					/>
				)}
				{isAiLoading && <IssueCreateAiLoading loadingType="bulk" />}

				<Stack space="space.100">
					<BulkCreateErrorSectionMessage />
					{currentJiraSite && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage {...messages.jiraSiteHeading} />
							</Text>
							<Text color="color.text.subtlest">{currentJiraSite.displayName}</Text>
						</Inline>
					)}
					{currentJiraProject && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage
									{...(expVal('project-terminology-refresh', 'isEnabled', false)
										? messages.jiraProjectHeadingGalaxia
										: messages.jiraProjectHeading)}
								/>
							</Text>
							<Text color="color.text.subtlest">{currentJiraProject.name}</Text>
						</Inline>
					)}
					<Box>
						<Button
							spacing="compact"
							testId="bulk-create-configure-button"
							onClick={onConfigureClick}
						>
							<FormattedMessage {...messages.bulkCreateConfigureButtonText} />
						</Button>
					</Box>
					{hasUnmappedRequiredFields && (
						<SectionMessage title={messages.requiredFieldsSectionMessageTitle.defaultMessage}>
							<Text>
								{fg('confluence-issue-terminology-refresh') ? (
									<FormattedMessage
										{...messages.requiredFieldsSectionMessageDescriptionIssueTermRefresh}
									/>
								) : (
									<FormattedMessage {...messages.requiredFieldsSectionMessageDescription} />
								)}
							</Text>
						</SectionMessage>
					)}
					{hasUnmappedWorkflowValidatorFields &&
						fg('confluence-jira-work-create-workflow-validators') && (
							<SectionMessage
								title={messages.workflowValidatorFieldsSectionMessageTitle.defaultMessage}
							>
								<Stack space="space.100">
									<Text>
										<FormattedMessage
											{...messages.workflowValidatorFieldsSectionMessageDescriptionIssueTermRefresh}
										/>
									</Text>
									<Box as="ul">
										{unmappedWorkflowValidatorFields.map((fieldName) => (
											<Box as="li" key={fieldName}>
												<Text>{fieldName}</Text>
											</Box>
										))}
									</Box>
								</Stack>
							</SectionMessage>
						)}
					{/* Manual bulk creation is disabled if there are more than 50 issues in the side panel*/}
					{(isCreatedFromTable ||
						(isCreatedFromActionItems && fg('confluence-create-work-items-from-action-items'))) &&
						manualCreationDisabled && (
							<SectionMessage
								title={messages.moreThanFiftyWorkItemsSectionMessageTitle.defaultMessage}
							>
								<Text>
									<FormattedMessage
										{...messages.moreThanFiftyWorkItemsSectionMessageDescription}
										values={{
											context: creationContext(),
										}}
									/>
								</Text>
							</SectionMessage>
						)}
				</Stack>
				<Stack space="space.100">
					{bulkIssuesList.map((issue) => (
						<BulkCreateIssueCard key={issue.id} issue={issue} issueType={issueType} />
					))}
				</Stack>
			</Stack>
		</Box>
	);
};
